// StatsBar — patrón global: las pastillas de arriba de la tabla (`.stats-bar` del mock). Además de
// contar filtran, así que son botones; la `KpiBar` (`.kpi-bar`) es la barra hermana que no lo es.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/** Los tonos con los que el mock pinta el número de la pastilla. */
export type StatsBarTone =
    | 'neutral'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'primary'
    | 'muted';

export interface StatsBarItem<K extends string = string> {
    /** Con qué recorte filtra la tabla al elegirla. */
    key: K;
    label: string;
    value: ReactNode;
    /** Color del número (default: `neutral`). */
    tone?: StatsBarTone;
    /** Informa pero no filtra: sigue enfocable para que su `title` diga por qué. */
    disabled?: boolean;
    /** Qué cuenta la pastilla, o por qué no filtra. */
    title?: string;
}

export interface StatsBarProps<K extends string> {
    items: readonly StatsBarItem<K>[];
    /** La pastilla prendida; varias cuando dos filtros conviven, ninguna con `null`. */
    active: K | readonly K[] | null;
    onChange: (key: K) => void;
    /** Cómo anuncia el lector de pantalla al grupo. Sin él, las pastillas van sueltas. */
    ariaLabel?: string;
    className?: string;
}

const VALUE_TONE_CLASS: Record<StatsBarTone, string> = {
    neutral: 'text-foreground',
    success: 'text-success',
    danger: 'text-danger',
    warning: 'text-warning',
    info: 'text-info',
    primary: 'text-primary',
    muted: 'text-muted-foreground',
};

/** Las pastillas se reparten el renglón en partes iguales (`flex: 1 1 0` del mock). */
const PILL =
    'inline-flex min-w-0 flex-1 basis-0 items-baseline justify-center gap-[5px] whitespace-nowrap rounded-[5px] border px-[9px] py-1 transition-colors aria-disabled:cursor-default aria-disabled:opacity-70';

function isActiveKey<K extends string>(active: K | readonly K[] | null, key: K): boolean {
    if (active === null) return false;
    return typeof active === 'string' ? active === key : active.includes(key);
}

export function StatsBar<K extends string>({
    items,
    active,
    onChange,
    ariaLabel,
    className,
}: StatsBarProps<K>) {
    return (
        <div
            role={ariaLabel === undefined ? undefined : 'group'}
            aria-label={ariaLabel}
            className={cn(
                'flex flex-none items-center gap-1 border-b border-border bg-muted/15 px-3 py-1.5',
                className,
            )}
        >
            {items.map((item) => {
                const locked = item.disabled === true;
                const isActive = !locked && isActiveKey(active, item.key);
                return (
                    <button
                        key={item.key}
                        type="button"
                        // `aria-disabled` y no `disabled`: enfocable, así el lector llega a su `title`.
                        aria-disabled={locked || undefined}
                        aria-pressed={isActive}
                        title={item.title}
                        onClick={() => {
                            if (locked) return;
                            onChange(item.key);
                        }}
                        className={cn(
                            PILL,
                            isActive
                                ? 'border-primary bg-accent'
                                : 'border-border bg-card hover:bg-accent/50',
                        )}
                    >
                        <span
                            className={cn(
                                'truncate text-[9.5px] font-bold uppercase tracking-[0.05em]',
                                isActive ? 'text-foreground' : 'text-muted-foreground',
                            )}
                        >
                            {item.label}
                        </span>
                        <span
                            className={cn(
                                'text-[12px] font-bold tabular-nums',
                                VALUE_TONE_CLASS[item.tone ?? 'neutral'],
                            )}
                        >
                            {item.value}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
